<template>
  <div class="page">
    <div class="seiten-kopf">
      <router-link to="/einkaufsliste" class="seiten-kopf-zurueck" aria-label="Zurück">
        <font-awesome-icon icon="fa-solid fa-arrow-left" />
      </router-link>
      <h1>Einkaufsliste</h1>
      <button type="button" class="hinzufuegen-fab" @click="PickerOeffnen" aria-label="Zutat hinzufügen">
        <font-awesome-icon icon="fa-solid fa-plus" />
      </button>
    </div>

    <div v-if="Einkaufslisten.length > 1" class="listen-wahl">
      <font-awesome-icon icon="fa-solid fa-list" class="listen-wahl-icon" />
      <select :value="AktiveEinkaufsliste.Id" @change="ListeWechseln($event)" class="listen-wahl-select" aria-label="Einkaufsliste wählen">
        <option v-for="Liste in Einkaufslisten" :key="Liste.Id" :value="Liste.Id">{{ Liste.Name }}</option>
      </select>
    </div>

    <p v-if="Fehlermeldung" class="error-message">{{ Fehlermeldung }}</p>

    <div v-if="Offene.length === 0" class="leer-zustand">
      <span class="leer-icon">
        <font-awesome-icon icon="fa-solid fa-basket-shopping" />
      </span>
      <p class="leer-titel">Deine Einkaufsliste ist leer</p>
      <p class="leer-text">Füge Zutaten aus deinem Katalog hinzu – ohne die Seite zu verlassen.</p>
      <button type="button" class="btn btn-primary leer-cta" @click="PickerOeffnen">
        <span class="btn-inhalt">
          <font-awesome-icon icon="fa-solid fa-plus" />
          Zutat hinzufügen
        </span>
      </button>
    </div>

    <template v-else>
      <div class="liste-suche">
        <input v-model="Suche" type="text" placeholder="Suchen..." class="liste-suche-feld" />
        <button type="button" class="btn btn-secondary liste-falten-btn" @click="FoldUnfoldAlle">
          {{ AlleEingeklappt ? 'Ausklappen' : 'Einklappen' }}
        </button>
      </div>
      <p class="geste-hinweis">Tippen = gekauft &nbsp;·&nbsp; Halten = entfernen &nbsp;·&nbsp; − / + = Anzahl</p>
      <p v-if="Gruppen.length === 0" class="hint-message">Keine Einträge gefunden.</p>
      <div v-else class="eintrag-liste-scroll">
        <div class="kategorie-gruppe" v-for="Gruppe in Gruppen" :key="Gruppe.Kategorie">
          <h2 class="kategorie-titel" @click="KategorieToggeln(Gruppe.Kategorie)">
            <span>{{ Gruppe.Kategorie }}</span>
            <span class="kategorie-badge">{{ Gruppe.Eintraege.length }}</span>
            <font-awesome-icon icon="fa-solid fa-chevron-right" class="kategorie-pfeil" :class="{ 'kategorie-pfeil--offen': !GeklapptKategorien.includes(Gruppe.Kategorie) }" />
          </h2>
          <div v-show="!GeklapptKategorien.includes(Gruppe.Kategorie)" class="eintrag-liste">
            <div
              class="eintrag-zeile eintrag-zeile--geste einkauf-zeile"
              v-for="Eintrag in Gruppe.Eintraege"
              :key="Eintrag.Id"
              @click="EintragKlick(Eintrag)"
              @touchstart.passive="DruckStart(Eintrag)"
              @touchend="DruckEnde"
              @touchcancel="DruckEnde"
              @mousedown="DruckStart(Eintrag)"
              @mouseup="DruckEnde"
              @mouseleave="DruckEnde"
              @contextmenu.prevent
            >
              <div class="eintrag-mit-bild">
                <img
                  v-if="Eintrag.HatBild"
                  :src="ZutatBildUrl(Eintrag)"
                  class="mini-bild"
                  alt=""
                  loading="lazy"
                  @error="$event.target.style.display = 'none'"
                />
                <div class="eintrag-info">
                  <span class="eintrag-name">{{ Eintrag.Name }}</span>
                  <span class="eintrag-details">{{ MengeText(Eintrag) }}</span>
                  <span v-if="Eintrag.Quellen && Eintrag.Quellen.length > 0" class="eintrag-quelle">
                    Gehört zu: {{ Eintrag.Quellen.map(Q => Q.RezeptName).join(', ') }}
                  </span>
                </div>
              </div>
              <div
                class="stepper"
                @click.stop
                @mousedown.stop
                @mouseup.stop
                @touchstart.stop
                @touchend.stop
              >
                <button
                  type="button"
                  class="stepper-btn"
                  :disabled="Eintrag.Anzahl <= 1"
                  @click="AnzahlAendern(Eintrag, -1)"
                  aria-label="Anzahl verringern"
                >
                  <font-awesome-icon icon="fa-solid fa-minus" />
                </button>
                <span class="stepper-wert">{{ Eintrag.Anzahl }}</span>
                <button
                  type="button"
                  class="stepper-btn"
                  @click="AnzahlAendern(Eintrag, 1)"
                  aria-label="Anzahl erhöhen"
                >
                  <font-awesome-icon icon="fa-solid fa-plus" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <button type="button" class="btn btn-secondary leeren-button" @click="Leeren">Liste leeren</button>
    </template>

    <!-- Zutaten-Picker Bottom Sheet -->
    <Teleport to="body">
      <div v-if="PickerOffen" class="overlay" @click.self="PickerSchliessen">
        <div class="bottom-sheet" role="dialog" aria-modal="true" aria-label="Zutat zur Einkaufsliste hinzufügen">
          <div class="sheet-griff"></div>
          <h3 class="sheet-titel">Zutat hinzufügen</h3>
          <input
            ref="PickerSucheFeld"
            v-model="PickerSuche"
            type="text"
            placeholder="Zutat suchen…"
            class="liste-suche-feld"
            autocomplete="off"
          />
          <div class="picker-liste">
            <template v-if="GefilterteZutaten.length > 0">
              <button
                v-for="Zutat in GefilterteZutaten"
                :key="Zutat.Id"
                type="button"
                class="picker-eintrag"
                :class="{ 'picker-eintrag--drauf': AnzahlAufListe(Zutat.Id) > 0 }"
                @click="AusPickerHinzufuegen(Zutat)"
              >
                <img
                  v-if="Zutat.HatBild"
                  :src="ZutatBildUrl(Zutat)"
                  class="mini-bild"
                  alt=""
                  loading="lazy"
                  @error="$event.target.style.display = 'none'"
                />
                <span class="picker-info">
                  <span class="picker-name">{{ Zutat.Name }}</span>
                  <span class="picker-details">{{ BasisMengeText(Zutat) }}</span>
                </span>
                <span v-if="AnzahlAufListe(Zutat.Id) > 0" class="picker-anzahl" :key="AnzahlAufListe(Zutat.Id)">
                  {{ AnzahlAufListe(Zutat.Id) }}×
                </span>
                <span v-else class="picker-plus">
                  <font-awesome-icon icon="fa-solid fa-plus" />
                </span>
              </button>
            </template>
            <div v-else class="picker-kein-treffer">
              <p class="hint-message">
                {{ Zutaten.length === 0 ? 'Noch keine Zutaten im Katalog.' : 'Keine Zutat gefunden.' }}
              </p>
              <button
                v-if="PickerSuche.trim()"
                type="button"
                class="btn btn-secondary neu-zutat-link"
                @click="NeueZutatAusSucheOeffnen"
              >
                <font-awesome-icon icon="fa-solid fa-plus" />
                „{{ PickerSuche.trim() }}“ anlegen &amp; hinzufügen
              </button>
            </div>
          </div>
          <button type="button" class="btn btn-primary" @click="PickerSchliessen">Fertig</button>
        </div>
      </div>
    </Teleport>

    <!-- Neue Zutat aus der Live-Suche anlegen -->
    <Teleport to="body">
      <div v-if="NeueZutatOffen" class="modal-overlay" @click.self="NeueZutatSchliessen">
        <div class="modal-sheet">
          <div class="modal-kopf">
            <span class="modal-titel">Neue Zutat anlegen</span>
            <button class="modal-schliessen" @click="NeueZutatSchliessen" aria-label="Schließen">
              <font-awesome-icon icon="fa-solid fa-xmark" />
            </button>
          </div>
          <div class="neu-zutat-form">
            <label class="neu-zutat-label">Name *</label>
            <div class="input-field">
              <input v-model="NeueZutat.Name" type="text" placeholder="Zutatname" />
            </div>
            <label class="neu-zutat-label">Kategorie</label>
            <div class="input-field">
              <select v-model="NeueZutat.KategorieId">
                <option :value="null">– keine –</option>
                <option v-for="Kat in Kategorien" :key="Kat.Id" :value="Kat.Id">{{ Kat.Name }}</option>
              </select>
            </div>
            <div class="neu-zutat-menge-zeile">
              <div class="input-field neu-zutat-menge">
                <input v-model.number="NeueZutat.Menge" type="number" min="0" step="any" placeholder="Menge" />
              </div>
              <div class="input-field neu-zutat-einheit">
                <select v-model="NeueZutat.Einheit">
                  <option value="Gramm">Gramm</option>
                  <option value="Kg">Kg</option>
                  <option value="ml">ml</option>
                  <option value="Liter">Liter</option>
                  <option value="Volumen">Volumen</option>
                  <option value="Stück">Stück</option>
                  <option value="Flasche">Flasche</option>
                  <option value="Packung">Packung</option>
                  <option value="EL">EL</option>
                  <option value="Netz">Netz</option>
                </select>
              </div>
            </div>
            <label class="neu-zutat-label">Foto</label>
            <BildAuswahl v-model="NeueZutatBild" />
            <p v-if="NeueZutatFehler" class="error-message">{{ NeueZutatFehler }}</p>
          </div>
          <div class="modal-fuss">
            <button type="button" class="btn btn-primary btn-breit" @click="NeueZutatSpeichern" :disabled="NeueZutatLaedt">
              Anlegen &amp; zur Einkaufsliste hinzufügen
            </button>
            <button type="button" class="btn btn-secondary btn-breit" @click="NeueZutatSchliessen">
              Abbrechen
            </button>
          </div>
        </div>
      </div>
    </Teleport>

  </div>
</template>

<script>
import {
  Einkaufslistenpositionen,
  EinkaufslisteLaden,
  Einkaufslisten,
  EinkaufslistenLaden,
  AktiveEinkaufsliste,
  AktiveEinkaufslisteSetzen,
  EinkaufslisteFinden,
  Zutaten,
  ZutatenLaden,
  ZutatFinden,
  Kategorien,
  KategorienLaden,
  EinkaufslistenEintragAlsGekauftMarkieren,
  EinkaufslistenEintragEntfernen,
  EinkaufslistenpositionAnzahlAendern,
  ZutatZurEinkaufslisteHinzufuegen,
  EinkaufslisteLeeren,
  ZutatHinzufuegen as NeueZutatAnlegen,
  ZutatBildSpeichern
} from '../composables/useZutaten'
import { BildUrl } from '../composables/useBilder'
import BildAuswahl from '../components/BildAuswahl.vue'

export default {
  name: 'EinkaufslisteAnzeigenView',
  components: { BildAuswahl },
  data() {
    return {
      Einkaufslistenpositionen,
      Einkaufslisten,
      AktiveEinkaufsliste,
      Zutaten,
      Kategorien,
      Fehlermeldung: '',
      Suche: '',
      GeklapptKategorien: [],
      LangdruckTimer: null,
      WarLangdruck: false,
      PickerOffen: false,
      PickerSuche: '',
      // Neue Zutat inline aus der Live-Suche anlegen
      NeueZutatOffen: false,
      NeueZutat: { Name: '', KategorieId: null, Menge: null, Einheit: 'Gramm' },
      NeueZutatBild: undefined,
      NeueZutatFehler: '',
      NeueZutatLaedt: false
    }
  },
  async created() {
    await Promise.all([KategorienLaden(), ZutatenLaden(), EinkaufslisteLaden(), EinkaufslistenLaden()])
  },
  computed: {
    Offene() {
      // Name/KategorieId immer live von der Zutat ableiten, nicht die beim Laden mitgelieferte Momentaufnahme verwenden -
      // sonst zeigt die Liste nach einer Zutat-Bearbeitung weiterhin die alte Kategorie/den alten Namen (bis zum nächsten Reload).
      return this.Einkaufslistenpositionen
        .filter(Eintrag => !Eintrag.Gekauft && Eintrag.EinkaufslisteId === this.AktiveEinkaufsliste.Id)
        .map(Eintrag => {
          const Zutat = ZutatFinden(Eintrag.ZutatId)
          return {
            ...Eintrag,
            Anzahl: Eintrag.Anzahl || 1,
            Name: Zutat ? Zutat.Name : Eintrag.Name,
            KategorieId: Zutat ? Zutat.KategorieId : Eintrag.KategorieId,
            HatBild: Zutat ? Zutat.HatBild : 0,
            BildVersion: Zutat ? Zutat.BildVersion : null
          }
        })
    },
    GefilterteOffene() {
      const s = this.Suche.toLowerCase().trim()
      if (!s) return this.Offene
      return this.Offene.filter(e => e.Name.toLowerCase().includes(s))
    },
    AlleEingeklappt() {
      return this.Gruppen.length > 0 && this.GeklapptKategorien.length >= this.Gruppen.length
    },
    Gruppen() {
      const Basis = this.GefilterteOffene
      const Gruppen = Kategorien
        .map(Kategorie => ({
          Kategorie: Kategorie.Name,
          Eintraege: Basis.filter(Eintrag => Eintrag.KategorieId === Kategorie.Id)
        }))
        .filter(Gruppe => Gruppe.Eintraege.length > 0)

      const OhneKategorie = Basis.filter(Eintrag => !Eintrag.KategorieId)
      if (OhneKategorie.length > 0) {
        Gruppen.push({ Kategorie: 'Ohne Kategorie', Eintraege: OhneKategorie })
      }

      return Gruppen
    },
    GefilterteZutaten() {
      const s = this.PickerSuche.toLowerCase().trim()
      if (!s) return this.Zutaten
      return this.Zutaten.filter(z => z.Name.toLowerCase().includes(s))
    }
  },
  methods: {
    KategorieToggeln(Name) {
      const Index = this.GeklapptKategorien.indexOf(Name)
      if (Index === -1) {
        this.GeklapptKategorien.push(Name)
      } else {
        this.GeklapptKategorien.splice(Index, 1)
      }
    },
    FoldUnfoldAlle() {
      if (this.AlleEingeklappt) {
        this.GeklapptKategorien = []
      } else {
        this.GeklapptKategorien = this.Gruppen.map(g => g.Kategorie)
      }
    },
    MengeText(Eintrag) {
      const Menge = Eintrag.Menge != null ? +Eintrag.Menge : ''
      const Basis = `${Menge} ${Eintrag.Einheit || ''}`.trim()
      if (!Basis) return Eintrag.Anzahl > 1 ? `${Eintrag.Anzahl}×` : ''
      return Eintrag.Anzahl > 1 ? `${Eintrag.Anzahl} × ${Basis}` : Basis
    },
    BasisMengeText(Zutat) {
      const Menge = Zutat.Menge != null ? +Zutat.Menge : ''
      return `${Menge} ${Zutat.Einheit || ''}`.trim()
    },
    ZutatBildUrl(Eintrag) {
      return BildUrl('Zutat', Eintrag.ZutatId || Eintrag.Id, Eintrag.BildVersion)
    },
    ListeWechseln(Ereignis) {
      AktiveEinkaufslisteSetzen(Number(Ereignis.target.value))
    },
    AnzahlAufListe(ZutatId) {
      const Eintrag = this.Einkaufslistenpositionen.find(
        e => e.ZutatId === ZutatId && !e.Gekauft && e.EinkaufslisteId === this.AktiveEinkaufsliste.Id
      )
      return Eintrag ? (Eintrag.Anzahl || 1) : 0
    },
    async AnzahlAendern(Eintrag, Delta) {
      this.Fehlermeldung = ''
      const Neu = (Eintrag.Anzahl || 1) + Delta
      if (Neu < 1) return
      try {
        await EinkaufslistenpositionAnzahlAendern(Eintrag.Id, Neu)
      } catch (Fehler) {
        this.Fehlermeldung = Fehler.message
      }
    },
    PickerOeffnen() {
      this.PickerSuche = ''
      this.PickerOffen = true
      this.$nextTick(() => {
        this.$refs.PickerSucheFeld && this.$refs.PickerSucheFeld.focus()
      })
    },
    PickerSchliessen() {
      this.PickerOffen = false
    },
    async AusPickerHinzufuegen(Zutat) {
      this.Fehlermeldung = ''
      try {
        await ZutatZurEinkaufslisteHinzufuegen(Zutat.Id, this.AktiveEinkaufsliste.Id)
      } catch (Fehler) {
        this.Fehlermeldung = Fehler.message
        this.PickerOffen = false
      }
    },
    NeueZutatAusSucheOeffnen() {
      this.NeueZutat = { Name: this.PickerSuche.trim(), KategorieId: null, Menge: null, Einheit: 'Gramm' }
      this.NeueZutatBild = undefined
      this.NeueZutatFehler = ''
      this.NeueZutatOffen = true
    },
    NeueZutatSchliessen() {
      this.NeueZutatOffen = false
    },
    async NeueZutatSpeichern() {
      this.NeueZutatFehler = ''
      if (!this.NeueZutat.Name.trim()) {
        this.NeueZutatFehler = 'Name darf nicht leer sein.'
        return
      }
      this.NeueZutatLaedt = true
      try {
        const Neu = await NeueZutatAnlegen({
          Name: this.NeueZutat.Name.trim(),
          KategorieId: this.NeueZutat.KategorieId || null,
          Menge: this.NeueZutat.Menge || null,
          Einheit: this.NeueZutat.Einheit
        })
        if (typeof this.NeueZutatBild === 'string') {
          try {
            await ZutatBildSpeichern(Neu.Id, this.NeueZutatBild)
          } catch { /* bewusst ignoriert - Foto kann später über "Bearbeiten" nachgereicht werden */ }
        }
        await ZutatZurEinkaufslisteHinzufuegen(Neu.Id, this.AktiveEinkaufsliste.Id)
        this.NeueZutatOffen = false
        this.PickerSuche = ''
      } catch (Fehler) {
        this.NeueZutatFehler = Fehler.message
      } finally {
        this.NeueZutatLaedt = false
      }
    },
    DruckStart(Eintrag) {
      this.WarLangdruck = false
      this.LangdruckTimer = setTimeout(async () => {
        this.WarLangdruck = true
        this.LangdruckTimer = null
        if (confirm(`"${Eintrag.Name}" wirklich von der Einkaufsliste entfernen?`)) {
          this.Fehlermeldung = ''
          try {
            await EinkaufslistenEintragEntfernen(Eintrag.Id)
          } catch (Fehler) {
            this.Fehlermeldung = Fehler.message
          }
        }
      }, 600)
    },
    DruckEnde() {
      if (this.LangdruckTimer) {
        clearTimeout(this.LangdruckTimer)
        this.LangdruckTimer = null
      }
    },
    EintragKlick(Eintrag) {
      if (this.WarLangdruck) {
        this.WarLangdruck = false
        return
      }
      this.AlsGekauftMarkieren(Eintrag.Id)
    },
    async AlsGekauftMarkieren(Id) {
      this.Fehlermeldung = ''
      try {
        await EinkaufslistenEintragAlsGekauftMarkieren(Id)
      } catch (Fehler) {
        this.Fehlermeldung = Fehler.message
      }
    },
    async Leeren() {
      this.Fehlermeldung = ''
      const ListenName = EinkaufslisteFinden(this.AktiveEinkaufsliste.Id)?.Name || 'Einkaufsliste'
      if (confirm(`„${ListenName}" wirklich vollständig leeren?`)) {
        try {
          await EinkaufslisteLeeren(this.AktiveEinkaufsliste.Id)
        } catch (Fehler) {
          this.Fehlermeldung = Fehler.message
        }
      }
    }
  }
}
</script>

<style scoped>
/* ── Kopf mit Hinzufügen-Button ──────────────────────── */
.seiten-kopf {
  justify-content: flex-start;
}

.hinzufuegen-fab {
  margin-left: auto;
  width: 42px;
  height: 42px;
  border: none;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--color-primary), var(--akzent-tief));
  color: #fff;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  box-shadow: 0 6px 18px color-mix(in srgb, var(--color-primary) 40%, transparent);
  transition: transform 0.18s ease, box-shadow 0.3s ease;
}

.hinzufuegen-fab:hover {
  transform: translateY(-1px) scale(1.05);
  box-shadow: 0 8px 24px color-mix(in srgb, var(--color-primary) 50%, transparent);
}

.hinzufuegen-fab:active {
  transform: scale(0.92);
}

/* ── Listen-Auswahl ──────────────────────────────────── */
.listen-wahl {
  display: flex;
  align-items: center;
  gap: 8px;
  margin: 4px 0 12px;
  max-width: 320px;
  padding: 6px 12px;
  border: 1px solid var(--color-border);
  border-radius: 10px;
  background: var(--color-bg-alt);
}

.listen-wahl-icon {
  color: var(--color-text-muted);
  font-size: 0.85rem;
  flex-shrink: 0;
}

.listen-wahl-select {
  flex: 1;
  border: none;
  background-color: var(--color-bg-alt);
  color: var(--color-text);
  font-size: 0.95rem;
  font-weight: 600;
  outline: none;
  min-width: 0;
}

/* ── Leerer Zustand ──────────────────────────────────── */
.leer-zustand {
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 48px 24px;
  gap: 4px;
}

.leer-icon {
  width: 72px;
  height: 72px;
  border-radius: 24px;
  background: var(--akzent-weich);
  color: var(--color-primary);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.7rem;
  margin-bottom: 14px;
}

.leer-titel {
  margin: 0;
  font-size: 1.05rem;
  font-weight: 700;
}

.leer-text {
  margin: 0;
  font-size: 0.88rem;
  color: var(--color-text-muted);
  max-width: 280px;
}

.leer-cta {
  margin-top: 20px;
  flex: unset;
  min-width: 220px;
}

/* ── Zeile mit Stepper ───────────────────────────────── */
.einkauf-zeile {
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}

.stepper {
  display: flex;
  align-items: center;
  gap: 2px;
  padding: 3px;
  background: var(--color-bg-alt);
  border: 1px solid var(--glas-rand);
  border-radius: 999px;
  flex-shrink: 0;
  cursor: default;
}

.stepper-btn {
  width: 30px;
  height: 30px;
  border: none;
  border-radius: 50%;
  background: transparent;
  color: var(--color-text);
  cursor: pointer;
  font-size: 0.72rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background-color 0.15s ease, color 0.15s ease, transform 0.12s ease;
}

.stepper-btn:hover:not(:disabled) {
  background: var(--akzent-weich);
  color: var(--color-primary);
}

.stepper-btn:active:not(:disabled) {
  transform: scale(0.85);
}

.stepper-btn:disabled {
  opacity: 0.25;
  cursor: default;
}

.stepper-wert {
  min-width: 24px;
  text-align: center;
  font-weight: 700;
  font-size: 0.9rem;
  font-variant-numeric: tabular-nums;
}

/* ── Picker Bottom Sheet ─────────────────────────────── */
.overlay {
  position: fixed;
  inset: 0;
  background: rgba(12, 12, 16, 0.45);
  -webkit-backdrop-filter: blur(6px);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: flex-end;
  z-index: 200;
  animation: Einblenden 0.25s ease both;
}

.bottom-sheet {
  background: var(--glas-stark);
  -webkit-backdrop-filter: var(--glas-blur);
  backdrop-filter: var(--glas-blur);
  border: 1px solid var(--glas-rand);
  border-radius: var(--radius-xl) var(--radius-xl) 0 0;
  padding: 12px 16px 32px;
  width: 100%;
  max-height: 82vh;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 12px;
  animation: SheetHoch 0.35s cubic-bezier(0.16, 1, 0.3, 1) both;
}

@media (min-width: 768px) {
  .overlay {
    justify-content: center;
    align-items: center;
  }
  .bottom-sheet {
    border-radius: var(--radius-xl);
    max-width: 480px;
    max-height: 80vh;
    padding: 24px;
  }
  .sheet-griff { display: none; }
}

.sheet-griff {
  width: 40px;
  height: 4px;
  background: var(--color-border);
  border-radius: 2px;
  margin: 0 auto 4px;
  flex-shrink: 0;
}

.sheet-titel {
  margin: 0;
  font-size: 1.1rem;
  font-weight: 700;
}

.picker-liste {
  display: flex;
  flex-direction: column;
  gap: 6px;
  overflow-y: auto;
  max-height: 48vh;
  min-height: 120px;
}

.picker-eintrag {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border: 1px solid var(--glas-rand);
  border-radius: var(--radius-md);
  background: var(--color-bg-alt);
  color: var(--color-text);
  cursor: pointer;
  font-family: inherit;
  text-align: left;
  transition: border-color 0.2s ease, transform 0.12s ease;
}

.picker-eintrag:hover {
  border-color: var(--akzent-ring);
}

.picker-eintrag:active {
  transform: scale(0.985);
}

.picker-eintrag--drauf {
  border-color: color-mix(in srgb, var(--color-primary) 35%, transparent);
}

.picker-info {
  display: flex;
  flex-direction: column;
  gap: 1px;
  min-width: 0;
}

.picker-name {
  font-weight: 600;
  font-size: 0.95rem;
}

.picker-details {
  font-size: 0.78rem;
  color: var(--color-text-muted);
}

.picker-anzahl {
  flex-shrink: 0;
  min-width: 34px;
  text-align: center;
  padding: 4px 10px;
  border-radius: 999px;
  background: var(--akzent-weich);
  color: var(--color-primary);
  font-weight: 700;
  font-size: 0.85rem;
  font-variant-numeric: tabular-nums;
  animation: Einblenden 0.2s ease both;
}

.picker-plus {
  flex-shrink: 0;
  width: 30px;
  height: 30px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  border: 1.5px dashed var(--color-border);
  color: var(--color-text-muted);
  font-size: 0.72rem;
  transition: border-color 0.2s ease, color 0.2s ease;
}

.picker-eintrag:hover .picker-plus {
  border-color: var(--color-primary);
  color: var(--color-primary);
}

.picker-kein-treffer {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 10px;
  padding: 8px 2px;
}

.neu-zutat-link {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex: unset;
}

/* ── Neue Zutat aus der Live-Suche anlegen (Modal) ───── */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(12, 12, 16, 0.45);
  -webkit-backdrop-filter: blur(6px);
  backdrop-filter: blur(6px);
  z-index: 210;
  display: flex;
  align-items: flex-end;
  animation: Einblenden 0.25s ease both;
}

.modal-sheet {
  width: 100%;
  max-height: 90dvh;
  background: var(--glas-stark);
  -webkit-backdrop-filter: var(--glas-blur);
  backdrop-filter: var(--glas-blur);
  border: 1px solid var(--glas-rand);
  border-radius: var(--radius-xl) var(--radius-xl) 0 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  animation: SheetHoch 0.35s cubic-bezier(0.16, 1, 0.3, 1) both;
}

@media (min-width: 768px) {
  .modal-overlay {
    justify-content: center;
    align-items: center;
  }
  .modal-sheet {
    border-radius: var(--radius-xl);
    max-width: 480px;
    max-height: 85vh;
  }
}

.modal-kopf {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 20px 12px;
  border-bottom: 1px solid var(--color-border);
  flex-shrink: 0;
}

.modal-titel {
  font-size: 1.1rem;
  font-weight: 600;
}

.modal-schliessen {
  width: 32px;
  height: 32px;
  border-radius: 6px;
  border: none;
  background: transparent;
  color: var(--color-text);
  cursor: pointer;
  font-size: 1.1rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.modal-fuss {
  padding: 16px;
  border-top: 1px solid var(--color-border);
  flex-shrink: 0;
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.btn-breit {
  width: 100%;
}

.neu-zutat-form {
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  overflow-y: auto;
}

.neu-zutat-label {
  font-size: 0.9rem;
  margin-top: 8px;
}

.neu-zutat-menge-zeile {
  display: flex;
  gap: 8px;
  margin-top: 4px;
}

.neu-zutat-menge {
  flex: 1;
}
</style>
